import { AGENDA } from '@utils/constants';
import type {
    CombinedLogicalDaysResponse,
    AgendaBooking,
    AgendaBookings,
    AgendaDiscount,
    AgendaDiscounts,
} from '@states/logicalDay';
import {
    convertDateToDayId,
    getDayRange,
    getDateFromDayId,
} from '@utils/dates/dates';
import type {
    LogicalDayHrResponses,
    LogicalDayPrResponses,
    LogicalDay,
} from '@kiute-tech/network-kiute-pro';
import { getPhysicalResourceCombinedId } from '@utils/network/resources';
import { logicalDay } from '@utils/network/network';
import type { AgendaResource } from '@states/resources';

export const createClosedDayActivity = (
    day: LogicalDay,
    id: number,
): AgendaBooking => {
    const { start, end } = getDayRange(getDateFromDayId(day.dayId));
    return ({
        objectType: AGENDA.CLOSED,
        startDate: start,
        endDate: end,
        id: Math.floor(Math.random() * 1000000),
        [AGENDA.GROUPING_NAME]: [id],
    } as unknown) as AgendaBooking;
};

export const resourceIdFromLogicalDay = (day: LogicalDay): number =>
    day?.humanResourceId ||
    getPhysicalResourceCombinedId(day.physicalResourceId, day.prUnitId);

export const transformLogicalDaysDiscounts = (
    days: LogicalDay[],
): AgendaDiscount[] =>
    days.reduce((acc, day) => {
        const id = resourceIdFromLogicalDay(day);
        const discounts: AgendaDiscount[] =
            day.discounts
                ?.map((discount) => ({
                    ...discount,
                    id: discount.id || Math.floor(Math.random() * 1000000),
                    startDate: new Date(Number(discount.range.start)),
                    endDate: new Date(Number(discount.range.end)),
                    [AGENDA.GROUPING_NAME]: [id],
                }))
                .filter((discount) => discount.discount !== 0) || [];
        return [...acc, ...discounts];
    }, [] as AgendaDiscount[]);

const mockRange = {
    startDate: new Date(315567003),
    endDate: new Date(315570003),
};

export const transformLogicalDaysActivities = (
    days: LogicalDay[],
): AgendaBooking[] =>
    days.reduce((acc, day) => {
        const id = resourceIdFromLogicalDay(day);
        const activities = day.activities.map((activity) => ({
            ...activity,
            ...(activity.range
                ? {
                    startDate: new Date(Number(activity.range.start)),
                    endDate: new Date(Number(activity.range.end)),
                }
                : mockRange),
            [AGENDA.GROUPING_NAME]: [id],
        }));

        return day.isOpenedDay
            ? [...acc, ...activities]
            : [...acc, ...activities, createClosedDayActivity(day, id)];
    }, [] as AgendaBooking[]);

export const mergeBookings = (
    act1: AgendaBooking,
    act2: AgendaBooking,
): AgendaBooking => ({
    ...act1,
    ...act2,
    [AGENDA.GROUPING_NAME]: [
        ...act1[AGENDA.GROUPING_NAME],
        ...act2[AGENDA.GROUPING_NAME],
    ],
});

export const combineHrAndPrActivities = (
    logicalDayHr: LogicalDay[],
    logicalDayPr: LogicalDay[],
): AgendaBookings => {
    const hrActivities = transformLogicalDaysActivities(logicalDayHr);
    const prActivities = transformLogicalDaysActivities(logicalDayPr);
    const allActivities = [...hrActivities, ...prActivities];
    return allActivities.reduce(
        (acc, act) => ({
            ...acc,
            [act.id]: acc?.[act.id] ? mergeBookings(act, acc[act.id]) : act,
        }),
        {} as AgendaBookings,
    );
};

export const combineHrAndPrDiscounts = (
    logicalDayHr: LogicalDay[],
    logicalDayPr: LogicalDay[],
): AgendaDiscounts => {
    const allDiscounts = [
        ...transformLogicalDaysDiscounts(logicalDayHr),
        ...transformLogicalDaysDiscounts(logicalDayPr),
    ];
    return allDiscounts.reduce(
        (acc, act) => ({
            ...acc,
            [act.id]: act,
        }),
        {} as AgendaDiscounts,
    );
};

export const formatLogicalDayParams = (
    selectedResources: AgendaResource[],
): {
    physicalResourceIds: string[];
    humanResourceIds: number[];
} =>
    selectedResources.reduce(
        (acc, agendaResource) => {
            if (agendaResource.resourceData.objectType === 'HumanResource') {
                acc.humanResourceIds = acc.humanResourceIds.concat(
                    agendaResource.resourceData.id,
                );
            }

            if (agendaResource.resourceData.objectType === 'PhysicalResource') {
                acc.physicalResourceIds = acc.physicalResourceIds.concat(
                    `${agendaResource.resourceData.id}-${
                        agendaResource.resourceData.prUnitId || -1
                    }`,
                );
            }

            return acc;
        },
        {
            physicalResourceIds: [],
            humanResourceIds: [],
        } as { physicalResourceIds: string[]; humanResourceIds: number[] },
    );

export const getLogicalDaysByResourceIds = async (
    fromDay: Date,
    toDay: Date,
    selectedResources: AgendaResource[],
): Promise<CombinedLogicalDaysResponse> => {
    const range = {
        fromDayId: convertDateToDayId(fromDay),
        toDayId: convertDateToDayId(toDay),
    };

    const params = formatLogicalDayParams(selectedResources);

    const promiseHr = params.humanResourceIds.length
        ? logicalDay.fetchLogicalDayByHrIds(range, params.humanResourceIds)
        : {};

    const promisePr = params.physicalResourceIds.length
        ? logicalDay.fetchLogicalDayByPrIds(range, params.physicalResourceIds)
        : {};

    return Promise.all([promiseHr, promisePr]).then((response) => {
        try {
            return {
                hr:
                    (response[0] as LogicalDayHrResponses)
                        .logicalDayHrGetResponses || [],
                pr:
                    (response[1] as LogicalDayPrResponses)
                        .logicalDayPrGetResponses || [],
            };
        } catch (_) {
            return {
                hr: [],
                pr: [],
            };
        }
    });
};
